import { Fragment } from 'react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Switch } from 'components/ui/switch';
import { ChipsInput, ChipsInputField, ChipsInputList } from 'components/ui/chips-input';
import { ChevronDown, ChevronRight, PlusCircle, Trash2 } from 'lucide-react';
import { ParameterFormField } from './utils/tree-utils';
import { PARAMETER_NAME_PLACEHOLDER } from './utils/naming-utils';
import { SYSTEM_VARIABLE_OPTIONS } from './utils/system-variable-utils';

const nestedObjectLevel = 3;

export type ParameterMobileListVariant = 'input' | 'output';

interface CreateApiParameterMobileListProps {
  variant: ParameterMobileListVariant;
  parameters: ParameterFormField[];
  parameterErrors: Record<string, string>;
  namingErrors: Record<string, string>;
  onToggleExpand: (id: string) => void;
  onParameterChange: (
    id: string,
    field: keyof ParameterFormField,
    value: string | boolean | string[],
  ) => void;
  onRemoveParameter: (id: string) => void;
  onAddChildParameter: (parentId: string, location: string) => void;
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <Label className="text-xs text-muted-foreground">{children}</Label>;
}

export function CreateApiParameterMobileList({
  variant,
  parameters,
  parameterErrors,
  namingErrors,
  onToggleExpand,
  onParameterChange,
  onRemoveParameter,
  onAddChildParameter,
}: CreateApiParameterMobileListProps) {
  const renderCards = (
    params: ParameterFormField[],
    level = 0,
    objectDepth = 0,
    parentType = '',
  ) => {
    return params.map((param) => {
      const isArrayItem = parentType === 'array';
      const canAddChild =
        variant === 'input'
          ? param.type === 'object' && level < 10
          : param.type === 'object' && level < 10;

      return (
        <Fragment key={param.id}>
          <div
            className="relative space-y-3 rounded-lg border border-border bg-card/50 p-4"
            style={{ marginLeft: level > 0 ? `${Math.min(level, 4) * 12}px` : undefined }}
          >
            <div className="absolute right-2 top-2 z-10 flex items-center gap-0.5">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => onRemoveParameter(param.id)}
                disabled={isArrayItem}
                aria-label="Remove parameter"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              {canAddChild && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-primary"
                  onClick={() =>
                    onAddChildParameter(
                      param.id,
                      variant === 'input' ? param.location : 'body',
                    )
                  }
                  aria-label="Add child parameter"
                >
                  <PlusCircle className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="flex items-start gap-2">
              {param.type === 'array' || param.type === 'object' ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="mt-0.5 h-8 w-8 shrink-0 p-0"
                  onClick={() => onToggleExpand(param.id)}
                >
                  {param.isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              ) : (
                <></>
                // <span className="w-8 shrink-0" aria-hidden />
              )}
              <div className="min-w-0 flex-1 space-y-1">
                <FieldLabel>Name</FieldLabel>
                <Input
                  placeholder={isArrayItem ? 'Array Item' : PARAMETER_NAME_PLACEHOLDER}
                  value={isArrayItem ? '' : param.name}
                  disabled={isArrayItem}
                  onChange={(e) => onParameterChange(param.id, 'name', e.target.value)}
                  className="h-9"
                />
                {/* {parameterErrors[param.id] && (
                  <p className="text-xs text-destructive">{parameterErrors[param.id]}</p>
                )}
                {namingErrors[param.id] && (
                  <p className="text-xs text-destructive">{namingErrors[param.id]}</p>
                )}
                {!isArrayItem && param.name.length >= 100 && (
                  <p className="text-[12px] text-destructive">Max 100 characters</p>
                )} */}
              </div>
            </div>

            <div className="space-y-1">
              <FieldLabel>Description</FieldLabel>
              <Input
                placeholder="Enter description"
                value={param.description}
                onChange={(e) => onParameterChange(param.id, 'description', e.target.value)}
                className="h-9"
              />
              {/* {(param.description || '').length >= 100 && (
                <p className="text-[12px] text-destructive">Max 100 characters</p>
              )} */}
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <FieldLabel>Type</FieldLabel>
                <Select
                  value={param.type}
                  onValueChange={(value) => onParameterChange(param.id, 'type', value)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="string">String</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="integer">Integer</SelectItem>
                    <SelectItem value="boolean">Boolean</SelectItem>
                    {level < 10 && !isArrayItem && <SelectItem value="array">Array</SelectItem>}
                    {objectDepth < nestedObjectLevel && (
                      <SelectItem value="object">Object</SelectItem>
                    )}
                    {variant === 'input' && !isArrayItem && (
                      <>
                        <SelectSeparator />
                        <SelectItem value="variable">System Variable</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {variant === 'input' && (
                <div className="space-y-1">
                  <FieldLabel>Location</FieldLabel>
                  <Select
                    value={param.location}
                    onValueChange={(value) => onParameterChange(param.id, 'location', value)}
                    disabled={level > 0}
                  >
                    <SelectTrigger className={`h-9 ${level > 0 ? 'opacity-50' : ''}`}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="query">Query</SelectItem>
                      <SelectItem value="body">Body</SelectItem>
                      <SelectItem value="header">Header</SelectItem>
                      <SelectItem value="path">Path</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {variant === 'input' && (
              <div className="space-y-1">
                <FieldLabel>Default Value</FieldLabel>
                {isArrayItem ? (
                  param.type !== 'object' && (
                    <ChipsInput
                      value={(() => {
                        try {
                          const parsed = param.defaultValue ? JSON.parse(param.defaultValue) : [];
                          return Array.isArray(parsed) ? parsed : [];
                        } catch {
                          return [];
                        }
                      })()}
                      onChange={(val) =>
                        onParameterChange(param.id, 'defaultValue', JSON.stringify(val))
                      }
                      className="max-h-[100px] min-h-[36px] overflow-y-auto"
                    >
                      <ChipsInputList />
                      <ChipsInputField />
                    </ChipsInput>
                  )
                ) : param.type === 'variable' ? (
                  <Select
                    value={param.defaultValue || SYSTEM_VARIABLE_OPTIONS[0].value}
                    onValueChange={(value) => onParameterChange(param.id, 'defaultValue', value)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select variable" />
                    </SelectTrigger>
                    <SelectContent>
                      {SYSTEM_VARIABLE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  param.type !== 'array' &&
                  param.type !== 'object' && (
                    <Input
                      placeholder="Enter default value"
                      value={param.defaultValue}
                      onChange={(e) =>
                        onParameterChange(param.id, 'defaultValue', e.target.value)
                      }
                      className="h-9"
                    />
                  )
                )}
              </div>
            )}

            <div className="flex items-center justify-between gap-3 border-t border-border pt-3">
              <FieldLabel>Required</FieldLabel>
              <Switch
                checked={param.required}
                onCheckedChange={(checked) => onParameterChange(param.id, 'required', checked)}
              />
            </div>
          </div>

          {(param.type === 'object' || param.type === 'array') &&
            param.isExpanded &&
            param.children &&
            renderCards(
              param.children,
              level + 1,
              param.type === 'object' ? objectDepth + 1 : objectDepth,
              param.type,
            )}
        </Fragment>
      );
    });
  };

  return <div className="space-y-3 md:hidden">{renderCards(parameters)}</div>;
}
